/**
 * @fileoverview Own the calendar workspace connecting event browsing to explicit event actions.
 *
 * Focused view/form/detail components consume this page's selection and filters.
 * Manual events and AI deadline proposals have separate state/action paths;
 * displaying a proposal does not approve publication.
 *
 * `calendarFormDates.js` owns local-date/all-day form semantics;
 * `kc-internal-backend/functions/calendar.py` owns persisted events and Google sync;
 * `kc-internal-backend/services/ai_calendar_actions.py` owns proposal transitions.
 * Navigation cache supports browsing without becoming the authority for edits.
 * Shared date semantics connect the form's inclusive ranges to provider event
 * ranges and timezone-aware timing.
 */

import { buildAllDayRange } from './calendarFormDates';
import './blue-theme.css';
import './styles.css';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import api from '../../api';
import Filters from './Filters';
import EventForm from './EventForm';
import CalendarInner from './CalendarInner';
import EventDetailsModal from './EventDetailsModal';
import EventCacheManager from './utils/eventCacheManager';

// Helper to generate a slug for calendarId and colorName


const slugify = (text) => {
  if (!text) return 'default'; // Default calendarId if no category_name
  return text.toString().toLowerCase()
    .replace(/\s+/g, '-')       // Replace spaces with -
    .replace(/[^\w-]+/g, '')  // Remove all non-word chars
    .replace(/--+/g, '-')     // Replace multiple - with single -
    .replace(/^-+/, '')          // Trim - from start of text
    .replace(/-+$/, '');         // Trim - from end of text
};

// Helper functions for color manipulation (moved outside the component)


const lightenDarkenColor = (col, amt) => {
  let usePound = false;
  if (col[0] === "#") {
    col = col.slice(1);
    usePound = true;
  }
  const num = parseInt(col, 16);
  let r = (num >> 16) + amt;
  if (r > 255) r = 255;
  else if (r < 0) r = 0;
  let b = ((num >> 8) & 0x00FF) + amt;
  if (b > 255) b = 255;
  else if (b < 0) b = 0;
  let g = (num & 0x0000FF) + amt;
  if (g > 255) g = 255;
  else if (g < 0) g = 0;
  return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16).padStart(6, '0');
};



const getContrastColor = (hexcolor) => {
  if (hexcolor.slice(0, 1) === '#') {
    hexcolor = hexcolor.slice(1);
  }
  const r = parseInt(hexcolor.substr(0, 2), 16);
  const g = parseInt(hexcolor.substr(2, 2), 16);
  const b = parseInt(hexcolor.substr(4, 2), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? '#000000' : '#ffffff';
};

const colorNameToHex = {
  'red': '#F44336',
  'green': '#4CAF50',
  'purple': '#9C27B0',
  'yellow': '#FFEB3B',
  'blue': '#2196F3',
  'grey': '#808080', // Added for default/fallback consistency
  // Add other color names if they exist in the database
};





export const filterActiveAssignees = (rawAssignees) => {
  const assigneesArr = Array.isArray(rawAssignees) ? rawAssignees : [];

  const coerceIsActive =

  (value) => {
    if (value === true || value === 1 || value === '1' || value === 'true' || value === 'TRUE') return true;
    if (value === false || value === 0 || value === '0' || value === 'false' || value === 'FALSE') return false;
    return undefined;
  };

  return assigneesArr.filter((a) => {
    const flagValue = a?.IsActive ?? a?.is_active ?? a?.isActive;
    const isActive = coerceIsActive(flagValue);
    return isActive !== false;
  });
};



function Calendar() {
    const { eventId: deepLinkEventId } = useParams();
    const navigate = useNavigate();
    const calendarInput = useSelector(state => state.calendarInput);
    const [events, setEvents] = useState([]);
    const [aiDeadlines, setAiDeadlines] = useState([]);
    const [showAiDeadlines, setShowAiDeadlines] = useState(true);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [assignees, setAssignees] = useState([]);
    const [cases, setCases] = useState([]);
    const [clients, setClients] = useState([]);
    const [clientMatterMap, setClientMatterMap] = useState({});
    const [categories, setCategories] = useState([]);
    const [isLoadingFilters, setIsLoadingFilters] = useState(true);
    


    const cacheManager = useRef(new EventCacheManager()).current;
    


    const getInitialFilters = () => {
      try {
        const savedFilters = localStorage.getItem('calendarFilters');
        if (savedFilters) {
          return JSON.parse(savedFilters);
        }
      } catch (error) {
        console.error('Error loading saved filters:', error);
      }
      return {
        category: '',
        assignee: '',
        case: '',
        client: '',
        eventType: '',
        responsibility: ''
      };
    };
    
    const [filters, setFilters] = useState(getInitialFilters());
    const [showEventForm, setShowEventForm] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isSavingEvent, setIsSavingEvent] = useState(false);
    const [syncNotice, setSyncNotice] = useState(null);
  


    const fetchAssignees = async () => {
      try {
        const response = await api.get('/assignees');
        setAssignees(filterActiveAssignees(response.data));
      } catch (error) {
        console.error('Error fetching assignees:', error);
      }
    };
  
    const fetchCases =

    async () => {
      try {
        const response = await api.get('/cases');
        setCases(response.data);
      } catch (error) {
        console.error('Error fetching cases:', error);
      }
    };

    const fetchCategories =

    async () => {
      try {
        const response = await api.get('/categories');
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    const fetchClients =

    async () => {
      try {
        const response = await api.get('/calendar-clients');
        setClients(response.data);
        // Build a reverse map: client_id -> Set of matter_numbers
        const map = {};
        response.data.forEach(c => {
          map[c.id] = new Set(c.matter_numbers);
        });
        setClientMatterMap(map);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };



    const fetchAiDeadlines = async () => {
      try {
        // Fetch both approved and pending approval deadlines
        const [approvedResponse, pendingResponse] = await Promise.all([
          api.get('/ai-calendar/deadlines', {
            params: { status: 'APPROVED', limit: 1000 }
          }),
          api.get('/ai-calendar/deadlines', {
            params: { status: 'PENDING_APPROVAL', limit: 1000 }
          })
        ]);
        


        const classificationCategoryMap = {
          OUR_DEADLINE: { name: 'Our Deadline', color: 'red' },
          OPPONENT_DEADLINE: { name: 'Opponent Deadline', color: 'green' },
          COURT_DEADLINE: { name: 'Court/Arbitrator Deadline', color: 'purple' },
          OUR_APPEARANCE_REQUIRED: { name: 'Our Appearance Required', color: 'yellow' },
          OTHER: { name: 'Anything Else', color: 'blue' },
        };

        const transformDeadline =

        (deadline) => {
          const category = classificationCategoryMap[deadline.deadline_classification] || { name: 'AI Deadlines', color: 'blue' };
          return {
            id: `ai-deadline-${deadline.id}`,
            title: `${deadline.deadline_type}${deadline.matter_number ? ' - ' + deadline.matter_number : ''}`,
            start: deadline.all_day
              ? deadline.deadline_date
              : `${deadline.deadline_date}T${deadline.start_time || '00:00:00'}`,
            end: deadline.all_day
              ? deadline.deadline_date
              : `${deadline.deadline_date}T${deadline.start_time || '00:00:00'}`,
            allDay: deadline.all_day,
            type: 'hard-deadline',
            confirmed: false,
            category_name: category.name,
            category_color: category.color,
            case_id: deadline.matter_number,
            case: deadline.matter_title || deadline.matter_number || null,
            location: deadline.location,
            description: deadline.description,
            assignees: deadline.primary_attorney_id ? [{
              attorney_id: deadline.primary_attorney_id,
              attorney_name: deadline.primary_attorney_name,
            }] : [],
            // AI-specific metadata
            source: 'ai-calendar',
            ai_status: deadline.status,
            ai_confidence: deadline.confidence_score,
            ai_reasoning: deadline.ai_reasoning,
            deadline_classification: deadline.deadline_classification,
            rule_applied: deadline.rule_applied,
            original_deadline_id: deadline.id
          };
        };
        
        const approvedDeadlines = approvedResponse.data.deadlines.map(transformDeadline);
        const pendingDeadlines = pendingResponse.data.deadlines.map(transformDeadline);
        const allDeadlines = [...approvedDeadlines, ...pendingDeadlines];
        
        setAiDeadlines(allDeadlines);
        console.log(`Fetched ${allDeadlines.length} AI deadlines (${approvedDeadlines.length} approved, ${pendingDeadlines.length} pending)`);
      } catch (error) {
        console.error('Error fetching AI deadlines:', error);
        // Silently fail - AI deadlines are supplementary
      }
    };



    const fetchRangeInBackground = useCallback(async (range) => {
      if (!range) return;
      
      const { start, end, key } = range;
      
      // Check if already pending to avoid duplicate fetches
      if (cacheManager.pendingFetches.has(key)) {
        return;
      }
      
      console.log(`[Prefetch] Fetching range: ${start.format('YYYY-MM-DD')} to ${end.format('YYYY-MM-DD')}`);
      
      try {
        cacheManager.markFetchPending(key);
        
        const response = await api.get('/events', {
          params: {
            start_date: start.toISOString(),
            end_date: end.toISOString()
          }
        });
        
        cacheManager.addEvents(response.data);
        cacheManager.markRangeFetched(start, end);
        
        // Silently update events state with the expanded cache
        // This won't trigger re-render if component is unmounted or view changed
        setEvents(cacheManager.getAllEvents());
        
        console.log(`[Prefetch] Completed range: ${start.format('YYYY-MM-DD')} to ${end.format('YYYY-MM-DD')}`);
      } catch (error) {
        console.error('[Prefetch] Error fetching range:', error);
      } finally {
        cacheManager.markFetchComplete(key);
      }
    }, [cacheManager]);



    const prefetchAdjacentPeriods = useCallback(async (viewType, currentDate) => {
      const prefetchRanges = cacheManager.calculatePrefetchRanges(viewType, currentDate);
      
      if (prefetchRanges.length === 0) {
        console.log('[Prefetch] No adjacent periods to prefetch (already cached)');
        return;
      }
      
      console.log(`[Prefetch] Starting prefetch of ${prefetchRanges.length} adjacent periods`);
      
      // Fetch all ranges in parallel (they're independent)
      await Promise.all(prefetchRanges.map(range => fetchRangeInBackground(range)));
      
      console.log('[Prefetch] All adjacent periods fetched, cache stats:', cacheManager.getStats());
    }, [cacheManager, fetchRangeInBackground]);



    const fetchEventsForDateRange = useCallback(async (viewType, currentDate) => {
      // Calculate what range we need based on the view
      const fetchRange = cacheManager.calculateFetchRange(viewType, currentDate);
      
      // Check if cache is stale (older than 60 minutes)
      const cacheIsStale = cacheManager.isCacheStale(60);
      
      // If range is cached and cache is fresh, skip fetch
      if (!fetchRange && !cacheIsStale) {
        console.log('Events for range already cached and fresh, using cache');
        // Update events from cache
        setEvents(cacheManager.getAllEvents());
        
        // Still prefetch adjacent periods even if current is cached
        // This handles the case where user navigated forward, then backward
        prefetchAdjacentPeriods(viewType, currentDate);
        return;
      }

      // If range is cached but stale, we'll refetch to get fresh data
      if (!fetchRange && cacheIsStale) {
        console.log('Cache is stale, refetching to get fresh data');
        // Clear the fetched ranges so we can fetch again
        cacheManager.fetchedRanges = [];
        // Recalculate fetch range
        const staleFetchRange = cacheManager.calculateFetchRange(viewType, currentDate);
        if (!staleFetchRange) {
          // Still couldn't calculate a range, just use cache
          setEvents(cacheManager.getAllEvents());
          return;
        }
        // Continue with fetch using the new range
        return fetchEventsForDateRange(viewType, currentDate);
      }

      const { start, end, key } = fetchRange;
      
      console.log(`Fetching events for range: ${start.format('YYYY-MM-DD')} to ${end.format('YYYY-MM-DD')}`);
      
      try {
        cacheManager.markFetchPending(key);
        
        // Fetch events with date range parameters
        const response = await api.get('/events', {
          params: {
            start_date: start.toISOString(),
            end_date: end.toISOString()
          }
        });
        
        // Add new events to cache (this will update existing events if they changed)
        cacheManager.addEvents(response.data);
        
        // Mark this range as fetched
        cacheManager.markRangeFetched(start, end);
        
        // Update state with all cached events
        setEvents(cacheManager.getAllEvents());
        
        console.log('Cache stats:', cacheManager.getStats());
        
        // After successfully fetching the current view, prefetch adjacent periods
        // This happens in the background and won't block the UI
        prefetchAdjacentPeriods(viewType, currentDate);
        
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        cacheManager.markFetchComplete(key);
      }
    }, [cacheManager, prefetchAdjacentPeriods]);



    const mergedEvents = useMemo(() => {
      if (showAiDeadlines) {
        return [...events, ...aiDeadlines];
      }
      return events;
    }, [events, aiDeadlines, showAiDeadlines]);
  


    const applyFilters = useCallback(() => {
      console.log("Applying filters:", filters);
      console.log("Original events:", mergedEvents);
      
      let filtered = [...mergedEvents];
  
      if (filters.category) {
        filtered = filtered.filter(event => event.category_name === filters.category);
        console.log(`After category filter (${filters.category}):`, filtered.length);
      }
  
      if (filters.assignee) {
        if (filters.assignee === 'unassigned') {
          filtered = filtered.filter(event => {
            const assigneesArr = Array.isArray(event.assignees) ? event.assignees : [];
            return assigneesArr.length === 0;
          });
        } else {
          const selectedId = String(filters.assignee);
          filtered = filtered.filter(event => {
            const assigneesArr = Array.isArray(event.assignees) ? event.assignees : [];
            return assigneesArr.some(a => String(a.attorney_id) === selectedId);
          });
        }
        console.log(`After assignee filter (${filters.assignee}):`, filtered.length);
      }
  
      if (filters.client) {
        const matterSet = clientMatterMap[filters.client];
        if (matterSet) {
          filtered = filtered.filter(event => event.case_id && matterSet.has(event.case_id));
        } else {
          filtered = [];
        }
        console.log(`After client filter (${filters.client}):`, filtered.length);
      }

      if (filters.case) {
        const q = String(filters.case).toLowerCase();
        filtered = filtered.filter(event => {
          const byCaseId = event.case_id ? String(event.case_id).toLowerCase().includes(q) : false;
          const byCaseField = event.case ? String(event.case).toLowerCase().includes(q) : false;
          return byCaseId || byCaseField;
        });
        console.log(`After case filter (query="${filters.case}"):`, filtered.length);
      }
  
      if (filters.eventType) {
        filtered = filtered.filter(event => event.type === filters.eventType);
        console.log(`After eventType filter (${filters.eventType}):`, filtered.length);
      }
  
      if (filters.responsibility) {
        if (filters.responsibility === 'confirmed') {
          filtered = filtered.filter(event => event.confirmed === true);
        } else if (filters.responsibility === 'unconfirmed') {
          filtered = filtered.filter(event => event.confirmed === false);
        }
        console.log(`After responsibility filter (${filters.responsibility}):`, filtered.length);
      }
  
      console.log("Final filtered events:", filtered);
      setFilteredEvents(filtered);
    }, [mergedEvents, filters, clientMatterMap]);



    useEffect(

    () => {
      // Load cached events immediately (loaded from localStorage in cache manager constructor)
      const cachedEvents = cacheManager.getAllEvents();
      if (cachedEvents.length > 0) {
        console.log(`[Cache] Displaying ${cachedEvents.length} cached events immediately`);
        setEvents(cachedEvents);
      }
      
      // Fetch filter options (these are still loaded upfront as they're small datasets)
      const fetchAllFilters =

      async () => {
        setIsLoadingFilters(true);
        await Promise.all([
          fetchAssignees(),
          fetchCases(),
          fetchCategories(),
          fetchClients()
        ]);
        setIsLoadingFilters(false);
      };
      fetchAllFilters();
      
      // Fetch AI-detected deadlines (approved deadlines from AI Calendar Agent)
      fetchAiDeadlines();
      
      // Note: Event fetching is now driven by calendar navigation in handleCalendarViewChange
      // The calendar will fetch fresh data when it determines what view/date to load
    }, [cacheManager]);

    // Deep-link: if URL contains /calendar/event/:eventId, fetch that event and open details modal
    const deepLinkHandled = useRef(false);
    useEffect(

    () => {
      if (!deepLinkEventId || deepLinkHandled.current) return;
      deepLinkHandled.current = true;

      const openDeepLinkedEvent =

      async () => {
        try {
          const response = await api.get(`/events/${deepLinkEventId}`);
          const event = response.data;
          // Add to cache so it's available if user closes modal and browses
          cacheManager.addEvents([event]);
          setEvents(cacheManager.getAllEvents());
          setSelectedEvent(event);
          setShowDetailsModal(true);
        } catch (error) {
          console.error('Error fetching deep-linked event:', error);
        }
      };
      openDeepLinkedEvent();
    }, [deepLinkEventId, cacheManager]);

    useEffect(() => {
      // Apply filters whenever events or filters change
      applyFilters();
    }, [applyFilters]);
  
    // Prepare unique category options for the Filters component
    const uniqueCategoryOptions = useMemo(() => {
      if (!events || events.length === 0) return [];
      const names = Array.from(new Set(events.map(event => event.category_name)))
        .filter(name => name); // Ensure no null or undefined names
      return names.map(name => ({ label: name, value: name }));
    }, [events]);
  


    const handleFilterChange = (filterName, value) => {
      const newFilters = {
        ...filters,
        [filterName]: value
      };
      setFilters(newFilters);
      
      // Persist filters to localStorage for better UX
      try {
        localStorage.setItem('calendarFilters', JSON.stringify(newFilters));
      } catch (error) {
        console.error('Error saving filters to localStorage:', error);
      }
    };



    const handleClearAllFilters = () => {
      const clearedFilters = {
        category: '',
        assignee: '',
        case: '',
        client: '',
        eventType: '',
        responsibility: ''
      };
      setFilters(clearedFilters);
      
      // Persist cleared state to localStorage
      try {
        localStorage.setItem('calendarFilters', JSON.stringify(clearedFilters));
      } catch (error) {
        console.error('Error saving filters to localStorage:', error);
      }
    };



    const handleCalendarViewChange = useCallback((viewType, currentDate) => {
      fetchEventsForDateRange(viewType, currentDate);
    }, [fetchEventsForDateRange]);
  
    const handleAddEvent =

    () => {
      setSelectedEvent(null);
      setShowEventForm(true);
    };
  
    const handleEventClick = useCallback(

    (event) => {
      setSelectedEvent(event);
      setShowDetailsModal(true);
    }, []);
  
    const handleEditFromDetails =

    () => {
      setShowDetailsModal(false);
      setShowEventForm(true);
    };
  
    const handleCloseForm =

    () => {
      setShowEventForm(false);
      setSelectedEvent(null); // Clear selected event on close
    };

    const handleCloseDetails =

    () => {
      setShowDetailsModal(false);
      setSelectedEvent(null);
      // If we arrived via deep link, clean URL back to /calendar
      if (deepLinkEventId) {
        navigate('/calendar', { replace: true });
      }
    }
  


    const handleSaveEvent = useCallback(async () => {
        const {
          assignedAttorneys,
          title,
          title2,
          notes,
          allday,
          date,
          daterange,
          selectedCategory,
          matterNumber,
          fileIds,
          createTask,
          addNow,
          selectedTrelloMember,
          trelloDate,
          trelloCardTitle,
          timeZone,
          softDeadlinesEnabled,
          softDeadlines,
        } = calendarInput;

        // Validate title
        const computedTitle = title2 && title2.trim() ? `${title} - ${title2}` : title;
        if (!computedTitle || !computedTitle.trim()) {
          console.error('Event title is required');
          alert('Please enter an event title');
          return;
        }

        // Validate and compute date range
        let startISO, endISO;
        if (allday) {
          // For all-day events, date must be set
          if (!date) {
            console.error('Date is required for all-day events');
            alert('Please select a date for the all-day event');
            return;
          }
          const range = buildAllDayRange(date);
          startISO = range.startISO;
          endISO = range.endISO;
        } else {
          // For timed events, daterange must be set
          if (!daterange || !daterange[0] || !daterange[1]) {
            console.error('Date range is required for timed events');
            alert('Please select a date and time range for the event');
            return;
          }

          // The DateTimeRangePicker returns dates in the browser's local timezone,
          // then .toISOString() converts to UTC using the browser's offset.
          // If the user selected a different timezone, we need to reinterpret:
          // extract the local date/time components and re-anchor them in the selected timezone.
          const selectedTz = timeZone || moment.tz.guess();
          const browserTz = moment.tz.guess();

          if (selectedTz !== browserTz) {
            // Reinterpret: extract the wall-clock time the user picked (in browser tz)
            // and treat it as if it were in the selected timezone instead.
            const reinterpret =

            (isoStr) => {
              const browserMoment = moment(isoStr);
              // Get the date/time components as they appear in the browser's timezone
              const formatted = browserMoment.format('YYYY-MM-DD HH:mm:ss');
              // Re-anchor those same components in the user's selected timezone
              return moment.tz(formatted, 'YYYY-MM-DD HH:mm:ss', selectedTz).utc().toISOString();
            };
            startISO = reinterpret(daterange[0]);
            endISO = reinterpret(daterange[1]);
          } else {
            startISO = daterange[0];
            endISO = daterange[1];
          }
        }

        // Final validation of computed dates
        if (!startISO || !endISO) {
          console.error('Failed to compute valid start/end dates');
          alert('Invalid date selection. Please try again.');
          return;
        }

        const eventData = {
          title: computedTitle,
          description: notes,
          start_date: startISO,
          end_date: endISO,
          all_day: allday,
          category_name: selectedCategory?.name || null,
          category_color: selectedCategory?.color || null,
          case_id: matterNumber || null,
          assignee_ids: (assignedAttorneys || []).map(a => a.id),
          fileIds: Array.isArray(fileIds) ? fileIds : [],
          // Trello task creation fields
          create_task: createTask || false,
          add_now: addNow || false,
          trello_member_id: selectedTrelloMember || null,
          trello_date: trelloDate || null,
          trello_card_title: trelloCardTitle || null,
        };

        // Append soft deadlines if enabled
        if (softDeadlinesEnabled && softDeadlines.length > 0) {
          eventData.soft_deadlines = softDeadlines.map(sd => {
            let sdStartISO, sdEndISO;
            if (sd.allday) {
              const range = buildAllDayRange(sd.date);
              sdStartISO = range.startISO;
              sdEndISO = range.endISO;
            } else {
              const selectedTz = timeZone || moment.tz.guess();
              const browserTz = moment.tz.guess();
              if (selectedTz !== browserTz && sd.daterange?.[0] && sd.daterange?.[1]) {
                const reinterpret =

                (isoStr) => {
                  const browserMoment = moment(isoStr);
                  const formatted = browserMoment.format('YYYY-MM-DD HH:mm:ss');
                  return moment.tz(formatted, 'YYYY-MM-DD HH:mm:ss', selectedTz).utc().toISOString();
                };
                sdStartISO = reinterpret(sd.daterange[0]);
                sdEndISO = reinterpret(sd.daterange[1]);
              } else {
                sdStartISO = sd.daterange?.[0];
                sdEndISO = sd.daterange?.[1];
              }
            }
            return {
              id: sd.id || null,
              title: sd.title,
              description: sd.notes,
              start_date: sdStartISO,
              end_date: sdEndISO,
              all_day: sd.allday,
              category_name: sd.selectedCategory?.name || null,
              category_color: sd.selectedCategory?.color || null,
              case_id: matterNumber || null,
              assignee_ids: (sd.assignedAttorneys || []).map(a => a.id),
            };
          });
        } else {
          eventData.soft_deadlines = [];
        }

    setIsSavingEvent(true);
    try {
      let response;
      if (selectedEvent) {
        // Update existing event
        response = await api.put(`/events/${selectedEvent.id}`, eventData);
        // Update the event in the cache
        cacheManager.updateEvent(response.data);
      } else {
        // Create new event
        response = await api.post('/events', eventData);
        // Add the new event to the cache
        cacheManager.addEvents([response.data]);
      }

      // Add soft deadline events to cache so they appear on the calendar
      if (response.data.soft_deadlines && response.data.soft_deadlines.length > 0) {
        cacheManager.addEvents(response.data.soft_deadlines);
      }
      
      // Update state with all cached events.
      // The useEffect on [applyFilters] will reactively reapply filters
      // when mergedEvents changes, so no manual call is needed.
      setEvents(cacheManager.getAllEvents());

      const pending = [response.data, ...(response.data.soft_deadlines || [])].some(event => event.google_sync?.status === 'pending');
      setSyncNotice(pending ? 'Saved here. Google Calendar changes and invitations are pending and will be retried automatically.' : null);
      setShowEventForm(false);
    } catch (error) {
      console.error('Error saving event:', error);
      alert(`Error saving event: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsSavingEvent(false);
    }
}, [selectedEvent, calendarInput, cacheManager]);
  
  const handleDeleteEvent = useCallback(

  async (eventId) => {
    try {
      const response = await api.delete(`/events/${eventId}`);
      setSyncNotice(response.data?.google_sync?.status === 'pending' ? response.data.google_sync.message : null);
      // Remove from cache
      cacheManager.removeEvent(eventId);
      // Update state with all cached events.
      // The useEffect on [applyFilters] will reactively reapply filters
      // when mergedEvents changes, so no manual call is needed.
      setEvents(cacheManager.getAllEvents());
    } catch (error) {
      console.error('Error deleting event:', error);
      alert(`Error deleting event: ${error.response?.data?.error || error.message}`);
    }
  }, [cacheManager]);
  
  const handleConfirmResponsibility = useCallback(

  async (eventId) => {
    try {
      const response = await api.post(`/events/${eventId}/confirm`);
      // Update the event in the cache
      cacheManager.updateEvent(response.data);
      // Update state with all cached events
      setEvents(cacheManager.getAllEvents());
    } catch (error) {
      console.error('Error confirming responsibility:', error);
    }
  }, [cacheManager]);



  const handleApproveAiDeadline = useCallback(async (eventId, replacement = null) => {
    try {
      // Extract the original deadline ID from the prefixed event ID (e.g., "ai-deadline-123" -> 123)
      const originalId = eventId.replace('ai-deadline-', '');
      
      const response = await api.post(`/ai-calendar/deadlines/${originalId}/approve`, {
        approved_by: 'Calendar UI', replacement
      });
      
      // Remove the AI deadline from local state (it's now SYNCED, not shown in AI list)
      setAiDeadlines(prev => prev.filter(deadline => deadline.id !== eventId));
      
      // Add the newly created calendar event to the cache
      if (response.data.event) {
        for (const replacedId of response.data.replaced_event_ids || []) cacheManager.removeEvent(replacedId);
        setSyncNotice(response.data.google_sync_pending || response.data.event.google_sync?.status === 'pending' ? 'Saved here. Google Calendar changes and invitations are pending and will be retried automatically.' : null);
        cacheManager.addEvents([response.data.event]);
        setEvents(cacheManager.getAllEvents());
      }

      console.log(`AI deadline ${originalId} approved and converted to event ${response.data.event?.id}`);
    } catch (error) {
      console.error('Error approving AI deadline:', error);
      alert(`Error approving deadline: ${error.response?.data?.error || error.message}`);
      throw error;
    }
  }, [cacheManager]);



  const handleRejectAiDeadline = useCallback(async (eventId) => {
    try {
      // Extract the original deadline ID from the prefixed event ID (e.g., "ai-deadline-123" -> 123)
      const originalId = eventId.replace('ai-deadline-', '');
      
      await api.post(`/ai-calendar/deadlines/${originalId}/reject`, {
        rejected_by: 'Calendar UI',
        reason: 'Rejected from calendar frontend'
      });
      
      // Remove the rejected deadline from local state
      setAiDeadlines(prev => prev.filter(deadline => deadline.id !== eventId));
      
      console.log(`AI deadline ${originalId} rejected`);
    } catch (error) {
      console.error('Error rejecting AI deadline:', error);
      alert(`Error rejecting deadline: ${error.response?.data?.error || error.message}`);
    }
  }, []);
  
    // Dynamically create Schedule-X calendars configuration and transform events
    const { scheduleXCalendarsConfig, transformedEvents } = useMemo(

    () => {
      if (!filteredEvents || filteredEvents.length === 0) {
        return { scheduleXCalendarsConfig: {}, transformedEvents: [] };
      }

      const uniqueCategories = new Map(); // Stores category_name -> category_color_name (e.g., "red")
      filteredEvents.forEach(event => {
        if (event.category_name && event.category_color) {
          if (!uniqueCategories.has(event.category_name)) {
            uniqueCategories.set(event.category_name, event.category_color);
          }
        }
      });

      const sxCalendars = {};
      uniqueCategories.forEach(

      (colorName, categoryName) => { // colorName is "red", "green", etc.
        const calendarKey = slugify(categoryName);
        const lowerColorName = colorName.toLowerCase();
        const hexColor = colorNameToHex[lowerColorName] || colorNameToHex['grey'];

        sxCalendars[calendarKey] = {
          colorName: calendarKey,
          lightColors: {
            main: hexColor,
            container: lightenDarkenColor(hexColor, 40),
            onContainer: getContrastColor(lightenDarkenColor(hexColor, 40))
          },
          darkColors: {
            main: hexColor,
            container: lightenDarkenColor(hexColor, -80), // Darken significantly for contrast with light text
            onContainer: '#e6e6e6' // Explicitly set lighter text color for all events in dark mode
          }
        };
      });
      
      // Add a default calendar if any events lack category info, or just as a fallback
      if (!sxCalendars['default']) {
        const defaultHexColor = colorNameToHex['grey'];
        sxCalendars['default'] = {
          colorName: 'default',
          lightColors: { 
            main: defaultHexColor, 
            container: lightenDarkenColor(defaultHexColor, 40), 
            onContainer: getContrastColor(lightenDarkenColor(defaultHexColor, 40)) 
          },
          darkColors: { 
            main: defaultHexColor, 
            container: lightenDarkenColor(defaultHexColor, -20), 
            onContainer: getContrastColor(lightenDarkenColor(defaultHexColor, -20)) 
          }
        };
      }

      const tEvents = filteredEvents.map(event => {
        const calIdCandidate = event.category_name ? slugify(event.category_name) : 'default';
        return {
          ...event,
          // Ensure assignees are carried over for the details modal
          assignees: event.assignees || [], 
          people: event.assignees?.map(assignee => ({ id: assignee.attorney_id, name: assignee.attorney_name })) || [],
          // Preserve ISO 8601 timestamps to avoid timezone drift
          start: event.start,
          end: event.end,
          // Ensure that the calendarId actually exists in sxCalendars, otherwise use 'default'
          calendarId: sxCalendars[calIdCandidate] ? calIdCandidate : 'default'
        };
      });

      return { scheduleXCalendarsConfig: sxCalendars, transformedEvents: tEvents };
    }, [filteredEvents]); // Recompute when filteredEvents change
  
        return (
      <div className="app-container calendar-page">
        <Filters
          categoryOptions={uniqueCategoryOptions}
          assignees={assignees}
          cases={cases}
          clients={clients}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearAll={handleClearAllFilters}
          filteredEventsCount={filteredEvents.length}
          totalEventsCount={events.length}
          isLoading={isLoadingFilters}
          showAiDeadlines={showAiDeadlines}
          aiDeadlinesCount={aiDeadlines.length}
          onAiDeadlinesToggle={setShowAiDeadlines}
        />
  
        {syncNotice && <p role="status" className="calendar-sync-notice">{syncNotice}</p>}
        <div className="calendar-container">
          <CalendarInner 
            events={transformedEvents}
            scheduleXCalendarsConfig={scheduleXCalendarsConfig}
            onEditEvent={handleEventClick}
            onDeleteEvent={handleDeleteEvent}
            onConfirmResponsibility={handleConfirmResponsibility}
            onAddEvent={handleAddEvent}
            onViewChange={handleCalendarViewChange}
            assignees={assignees}
          />
        </div>
  
        {showEventForm && (
          <EventForm
            event={selectedEvent}
            categories={categories}
            assignees={assignees}
            cases={cases}
            onSave={handleSaveEvent}
            onClose={handleCloseForm}
            isSaving={isSavingEvent}
          />
        )}

        {showDetailsModal && (
          <EventDetailsModal
            event={selectedEvent}
            onClose={handleCloseDetails}
            onEdit={handleEditFromDetails}
            onDelete={handleDeleteEvent}
            onConfirmResponsibility={handleConfirmResponsibility}
            onApproveAiDeadline={handleApproveAiDeadline}
            onRejectAiDeadline={handleRejectAiDeadline}
          />
        )}
      </div>
    );
  }

export default Calendar;
